// Editable category tree — mirrors DraftTreeEditor from taxonomy but with
// live mutations instead of draft/save/activate cycle.
// Each node shows a right-click context menu for add child / edit / delete.
// Category type is passed through for display labels.

import { useState, useCallback } from 'react'
import {
  ChevronRight,
  ChevronDown,
  Plus,
  Pencil,
  Trash2,
} from 'lucide-react'
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from '@/components/ui/context-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { categoryObjectLabels, type CategoryType } from '@/lib/api/categories'
import type { CategoryTreeNode } from './types'

interface CategoryTreeEditorProps {
  roots: CategoryTreeNode[]
  categoryType: CategoryType
  onAddChild: (parentNode: CategoryTreeNode) => void
  onEdit: (node: CategoryTreeNode) => void
  onDelete: (node: CategoryTreeNode) => void
}

export function CategoryTreeEditor({
  roots,
  categoryType,
  onAddChild,
  onEdit,
  onDelete,
}: CategoryTreeEditorProps) {
  // Track expanded node IDs. Default: all depth-0 and depth-1 nodes expanded.
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    function expand(nodes: CategoryTreeNode[]) {
      for (const node of nodes) {
        if (node.children.length > 0 && node.depth <= 1) {
          initial.add(node.id)
          expand(node.children)
        }
      }
    }
    expand(roots)
    return initial
  })

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleAddChild = useCallback(
    (node: CategoryTreeNode) => {
      // Auto-expand parent so the new child is visible
      setExpanded((prev) => {
        const next = new Set(prev)
        next.add(node.id)
        return next
      })
      onAddChild(node)
    },
    [onAddChild],
  )

  if (roots.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No categories found. The root category should be created during tenant setup.
      </div>
    )
  }

  return (
    <div className="space-y-0.5">
      {roots.map((root) => (
        <CategoryNodeRow
          key={root.id}
          node={root}
          categoryType={categoryType}
          expanded={expanded}
          onToggle={toggle}
          onAddChild={handleAddChild}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}

interface CategoryNodeRowProps {
  node: CategoryTreeNode
  categoryType: CategoryType
  expanded: Set<string>
  onToggle: (id: string) => void
  onAddChild: (node: CategoryTreeNode) => void
  onEdit: (node: CategoryTreeNode) => void
  onDelete: (node: CategoryTreeNode) => void
}

function CategoryNodeRow({
  node,
  categoryType,
  expanded,
  onToggle,
  onAddChild,
  onEdit,
  onDelete,
}: CategoryNodeRowProps) {
  const hasChildren = node.has_children || node.children.length > 0
  const isExpanded = expanded.has(node.id)
  const isRoot = node.depth === 0
  const indent = node.depth * 24
  const objectLabel = categoryObjectLabels[categoryType]

  const canDelete = !isRoot && node.object_count === 0 && !node.has_children

  return (
    <div>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            className={`group flex items-center gap-1.5 rounded-sm py-1.5 pr-2 hover:bg-accent/50 ${
              node.depth === 0 ? 'font-semibold' : ''
            } ${node.depth === 1 ? 'text-sm font-medium' : ''} ${
              node.depth >= 2 ? 'text-sm text-muted-foreground' : ''
            }`}
            style={{ paddingLeft: `${indent + 4}px` }}
          >
            {/* Expand/collapse chevron */}
            {hasChildren ? (
              <button
                onClick={() => onToggle(node.id)}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm hover:bg-muted"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </button>
            ) : (
              <span className="w-5 shrink-0" />
            )}

            {/* Path segment — show only the final three-digit code */}
            <span className="w-10 shrink-0 text-xs tabular-nums text-muted-foreground/60">
              {node.path.split('.').pop()}
            </span>

            {/* Name with optional description tooltip */}
            {node.description ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default">{node.name}</span>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p className="text-sm">{node.description}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <span>{node.name}</span>
            )}

            {/* Metadata — always visible, right-aligned */}
            <div className="ml-auto flex items-center gap-4">
              <span className="w-36 truncate text-left text-xs text-muted-foreground">
                {node.owner_role_label}
              </span>
              <span className="w-32 truncate text-right text-xs italic text-muted-foreground/70">
                {node.owner_display_name ?? 'unassigned'}
              </span>
              <span className="w-8 text-right text-xs tabular-nums text-muted-foreground">
                {node.object_count}
              </span>
            </div>
          </div>
        </ContextMenuTrigger>

        <ContextMenuContent>
          <ContextMenuItem onClick={() => onAddChild(node)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Child
          </ContextMenuItem>
          {!isRoot && (
            <>
              <ContextMenuItem onClick={() => onEdit(node)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem
                onClick={() => onDelete(node)}
                className="text-destructive focus:text-destructive"
                disabled={!canDelete}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
                {node.has_children && ' (has children)'}
                {node.object_count > 0 && ` (has ${objectLabel})`}
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>

      {/* Children (only rendered when expanded) */}
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <CategoryNodeRow
              key={child.id}
              node={child}
              categoryType={categoryType}
              expanded={expanded}
              onToggle={onToggle}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  )
}
